import { Text } from '@nbc-design/text';
import Title from 'components/Title';
import { ProductFormSelect } from 'components/productFormFields';
import React, { FunctionComponent } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { InjectedIntl } from 'react-intl';
import { GenericProps, ProductForm } from 'types/interfaces';
import { getTextFactory } from 'utils/TextUtils';
import { createErrorFormatter, isJointMenu } from 'utils/productFormUtils';
import config from './config';
import FinancialInvolvement from './components/FinancialInvolvement/FinancialInvolvement';
import { EmployerPhoneNumber, Occupation, ProInvolvement } from './types';
import './styles.scss';
import ActiveOccupations from './components/ActiveOccupations/ActiveOccupations';

type EmploymentProps = GenericProps & {
  intl: InjectedIntl;
  productForm?: ProductForm;
  occupationType?: string | null;
  startOccupationDate?: string | null;
  employmentType?: string | null;
  employmentCode?: string | null;
  employerName?: string | null;
  employerPhoneNumber?: EmployerPhoneNumber;
  activeOccupations?: Occupation[];
  proInvolvement?: ProInvolvement;
  firmEmployeeQuestionInd?: boolean;
  professionalQuestionInd?: boolean;
  hasAdminInvolvement?: boolean;
  hasTenPercentInvolvement?: boolean;
  hasTwentyPercentInvolvement?: boolean;
  adminInvolvementList?: object[];
  tenPercentInvolvementList?: object[];
  twentyPercentInvolvementList?: object[];
};

const Employment: FunctionComponent<EmploymentProps> = (props: EmploymentProps) => {
  const { intl, occupationType = null, activeOccupations = [], change, meta }: EmploymentProps = props;

  const isJointStep = meta && meta.stepId === config.STEP_JOINT_ID;

  /**
   * Business rule: When the profile does not have a main occupation (no primary flag)
   * and there is only 1 occupation in MCP, BAO should automatically use that occupation
   * as the main occupation by pre-selecting its occupationType.
   */
  React.useEffect(() => {
    const hasNoMainOccupation = !activeOccupations.some((o) => o.primary);
    const hasOnlyOneOccupation = activeOccupations.length === 1;

    if (!occupationType && hasNoMainOccupation && hasOnlyOneOccupation) {
      change('occupationType', activeOccupations[0].occupationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOccupations]);

  const getText = getTextFactory(intl, 'employmentV2');
  const getGlobalText = getTextFactory(intl, 'global');

  const formatErrorMessage = (prefix?: string) => {
    return createErrorFormatter(intl, prefix);
  };

  return (
    <div className="employment__form">
      {isJointMenu(meta) && (
        <Text size="large" className={isJointStep ? 'joint-spouse-title' : 'joint-applicant-title'}>
          {isJointStep ? getGlobalText('spouseMenuTitle') : getGlobalText('applicantMenuTitle')}
        </Text>
      )}

      <Title label={getText('pageTitle')} subTitle={getText('pageSubTitle')} />

      <Row className="collection-wrapper">
        <Col xs={12} md={6} lg={6} data-test="field_occupation_type">
          <ProductFormSelect
            name="occupationType"
            label={getText('occupationType')}
            formatErrorMessage={formatErrorMessage()}
            placeholder={getText('selectOccupation')}
            sort={true}
          />
        </Col>
      </Row>

      {occupationType !== null && <ActiveOccupations {...props} />}

      <FinancialInvolvement {...props} />
    </div>
  );
};

export default Employment;
